import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Vietnamese mirror of the apostille vs legalisation guide. */
const TITLE = "Apostille và hợp pháp hoá lãnh sự khác nhau thế nào — hướng dẫn theo từng nước";
const DESC =
  "Cách chứng nhận giấy tờ Thái Lan để dùng ở nước ngoài: khác biệt giữa apostille và hợp pháp hoá lãnh sự, thứ tự đúng của các bước và lưu ý theo từng nước.";
const PATH = "/blog/apostille-vs-legalization-by-country";
const URL = `/vi${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Apostille là gì?",
    answer:
      "Là một giấy chứng nhận duy nhất theo Công ước La Hay, thay cho chuỗi hợp pháp hoá lãnh sự giữa các nước thành viên. Chỉ bỏ được bước đại sứ quán khi cơ quan tiếp nhận là thành viên và thực sự chấp nhận apostille.",
  },
  {
    question: "Hợp pháp hoá lãnh sự đi theo thứ tự nào?",
    answer:
      "Lấy bản gốc hoặc bản sao có chứng thực của cơ quan cấp, giấy tờ tư nhân thì công chứng qua luật sư Notary, sau đó chứng nhận tại Cục Lãnh sự Bộ Ngoại giao Thái Lan, rồi dịch, cuối cùng là hợp pháp hoá tại đại sứ quán nước tiếp nhận.",
  },
  {
    question: "Dịch trước hay chứng nhận trước?",
    answer:
      "Về nguyên tắc là chứng nhận trước rồi mới dịch. Nếu dịch trước, các con dấu và dòng chú thích được thêm về sau sẽ không có trong bản dịch và đó là lý do bị trả lại.",
  },
  {
    question: "Có thể dùng bản photocopy không?",
    answer:
      "Phần lớn là không. Cần bản gốc hoặc bản sao do cơ quan cấp chứng thực; bản photocopy thường không được tiếp nhận.",
  },
  {
    question: "Giấy tờ có thời hạn sử dụng không?",
    answer:
      "Nhiều cơ quan yêu cầu giấy tờ hộ tịch hoặc lý lịch tư pháp cấp trong vòng 1–6 tháng. Mỗi bước đều tính theo ngày làm việc, nên hãy đếm ngược từ hạn nộp.",
  },
  {
    question: "Có thể uỷ quyền cho người khác làm thay không?",
    answer:
      "Được, nhưng phạm vi uỷ quyền phải bao gồm cả xin, nhận, làm các bước chứng nhận và nộp. Phạm vi hẹp sẽ khiến quy trình dừng giữa đường.",
  },
  {
    question: "Nguyên nhân bị trả lại nhiều nhất là gì?",
    answer:
      "Làm sai thứ tự, thiếu bản dịch của con dấu và chú thích, tên viết không khớp giữa các giấy tờ, giấy tờ quá hạn, và dùng bản photocopy thường.",
  },
];

export const Route = createFileRoute("/vi/blog/apostille-vs-legalization-by-country")({
  component: ViApostilleVsLegalisation,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "vi" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "vi") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "apostille Thái Lan, hợp pháp hoá lãnh sự Thái Lan, chứng nhận giấy tờ Thái Lan, Cục Lãnh sự Bộ Ngoại giao Thái Lan, công chứng Notary Thái Lan",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "vi"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Trang chủ", url: "/vi" },
              { name: "Hướng dẫn", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const SEQUENCE: string[] = [
  "Hỏi trước cơ quan tiếp nhận về các lớp chứng nhận, ngôn ngữ được chấp nhận và thời hạn giấy tờ",
  "Lấy bản gốc hoặc bản sao có chứng thực của cơ quan cấp",
  "Giấy tờ tư nhân: công chứng chữ ký qua luật sư Notary",
  "Chứng nhận tại Cục Lãnh sự Bộ Ngoại giao khi được yêu cầu",
  "Dịch toàn văn, bao gồm con dấu, tem và mọi dòng chú thích",
  "Hợp pháp hoá tại đại sứ quán nước tiếp nhận, hoặc chuyển sang tuyến apostille nếu được chấp nhận",
];

const COUNTRIES: Array<{ c: string; note: string }> = [
  { c: "Úc", note: "Nhiều cơ quan yêu cầu bản dịch có chứng nhận NAATI; một số giấy tờ vẫn cần chứng nhận Cục Lãnh sự." },
  { c: "Hoa Kỳ", note: "Yêu cầu khác nhau theo tiểu bang và cơ quan; có thể cần thêm lớp chứng nhận ngoài công chứng." },
  { c: "Trung Quốc đại lục", note: "Thường yêu cầu chuỗi chứng nhận đầy đủ và bản dịch tương ứng chính xác với bản gốc." },
  { c: "Hàn Quốc và Nhật Bản", note: "Thông thường là Cục Lãnh sự rồi đến đại sứ quán; tên phải khớp với hộ chiếu." },
  { c: "Các nước châu Âu", note: "Nếu cơ quan tiếp nhận chấp nhận apostille thì có thể bỏ bước đại sứ quán." },
  { c: "Trung Đông", note: "Thường yêu cầu chuỗi hợp pháp hoá đầy đủ và chỉ định rõ ngôn ngữ bản dịch." },
];

const MISTAKES: string[] = [
  "Dịch trước khi chứng nhận nên bản dịch thiếu nội dung chứng nhận",
  "Dùng bản photocopy thường thay vì bản gốc hoặc bản sao chứng thực",
  "Tên viết không nhất quán giữa các giấy tờ trong cùng bộ hồ sơ",
  "Xin giấy tờ quá sớm nên đến lúc nộp đã hết thời hạn",
  "Phạm vi uỷ quyền quá hẹp, không bao gồm các bước chứng nhận và nộp",
  "Bỏ sót phần dịch của con dấu, tem và chú thích",
];

function ViApostilleVsLegalisation() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/vi" className="text-muted-foreground hover:text-foreground">
          Trang chủ
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Hướng dẫn
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="vi" />
      <p className="answer-summary speakable-summary">
        Giấy tờ Thái Lan bị trả lại thường không phải vì nội dung sai mà vì làm sai thứ tự chứng nhận, thiếu bản dịch
        con dấu, hoặc giấy tờ đã quá hạn. Hãy đếm ngược từ hạn nộp, xác nhận yêu cầu của cơ quan tiếp nhận, rồi đi theo
        thứ tự “bản gốc → công chứng → Cục Lãnh sự → dịch → đại sứ quán”.
      </p>

      <h2>1. Khác biệt giữa hai tuyến</h2>
      <p>
        Apostille là một giấy chứng nhận duy nhất thay cho cả chuỗi và được các nước thành viên công nhận lẫn nhau. Hợp
        pháp hoá lãnh sự đi từng bước và kết thúc tại cơ quan đại diện của nước tiếp nhận. Tiêu chí quyết định chỉ có
        một: hiện tại cơ quan tiếp nhận chấp nhận tuyến nào — hãy kiểm tra bằng hướng dẫn mới nhất.
      </p>

      <h2>2. Thứ tự đúng</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Lưu ý theo từng nước</h2>
      <ul>
        {COUNTRIES.map((x) => (
          <li key={x.c}>
            <strong>{x.c}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>4. Những lý do bị trả lại thường gặp</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. Câu hỏi thường gặp</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. Nguồn tham khảo và dịch vụ liên quan</h2>
      <p className="text-sm">
        Tham khảo:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Cục Lãnh sự, Bộ Ngoại giao Thái Lan
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" target="_blank" rel="noreferrer">
          Công ước Apostille (HCCH)
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/vi/lgz" className="text-primary underline">
            Dịch vụ chứng nhận và hợp pháp hoá giấy tờ
          </Link>
        </li>
        <li>
          <Link to="/vi/notary" className="text-primary underline">
            Dịch vụ công chứng Notary Public
          </Link>
        </li>
        <li>
          <Link to="/vi/naati" className="text-primary underline">
            Dịch thuật có chứng nhận NAATI
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Nội dung rà soát ngày {PUBLISHED}. Đây là thông tin chung để chuẩn bị hồ sơ, không bảo đảm quyết định của bất kỳ
        cơ quan nào. Vui lòng xác nhận yêu cầu mới nhất với cơ quan cấp và cơ quan tiếp nhận trước khi thực hiện. Nếu
        bạn không muốn tự theo từng bước, đội ngũ hơn 15 năm kinh nghiệm của chúng tôi có thể tư vấn và thực hiện toàn
        bộ quy trình — liên hệ qua điện thoại, LINE hoặc email.
      </p>
    </article>
  );
}
